import moment from "moment";
import { Fragment, useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import {
  FacebookIcon,
  FacebookShareButton,
  LinkedinIcon,
  TwitterIcon,
} from "react-share";
import { sendTrackingEvent } from "../../../helpers/tracking_management";
import useTriggerSnackbar from "../../../hooks/useTriggerSnackbar";
import {
  AdPlacement,
  ArticleContent,
  ArticleDetails,
  CustomIconContainer,
  GridArticleContent,
  GridArticleDetails,
  GridContent,
  GridShareIcons,
  GridShortDescription,
  GridTitle,
  ImageAdBanner,
  LinkIconStyled,
  LinkStyled,
  LinkedinIconStyled,
  ShortDescription,
  TitleStyled,
  TwitterIconStyled,
  useStyles,
} from "./styles";

export default function AdviceArticle(props) {
  const classes = useStyles();
  const { blog, adviceAd, ArticleContentRef } = props;
  const [triggerSnackbarFunc] = useTriggerSnackbar();
  // To check when advice advertisement banner is in view
  const [ref, inView] = useInView({
    threshold: 0,
  });
  const [shareUrl, setShareUrl] = useState("");
  const title = `Check out this article about ${blog?.attributes?.title}`;

  // GTM tracking custom event for tracking advice advertisement banner views
  function triggerCustomEvent() {
    if (inView) {
      sendTrackingEvent({
        event: "CE_view-ad-banner-advice-page",
        "blog-slug": `${blog?.attributes?.slug}`,
      });
    }
  }

  triggerCustomEvent();

  useEffect(() => {
    setShareUrl(
      `${process.env.NEXT_PUBLIC_DOMAIN}/advice/${blog?.attributes?.slug}`
    );
    sendTrackingEvent({
      event: "CE_advice_view",
      "blog-slug": `${blog?.attributes?.slug}`,
    });
  }, [blog?.attributes?.slug]);

  function handleAdBannerClick() {
    if (!adviceAd) return;

    const adTitle = adviceAd?.attributes?.title;
    const ctaButton = adviceAd?.attributes?.ctaButtonText;
    const url = adviceAd?.attributes?.url;
    const blogSlug = blog?.attributes?.slug;

    // GTM tracking custom event for tracking advice advertisement banner clicks
    sendTrackingEvent({
      event: "CE_click-ad-banner-advice-page",
      title: adTitle,
      "cta-button": ctaButton,
      url: url,
      "blog-slug": blogSlug,
    });
  }

  function onCopyToClipboard() {
    if (!navigator.clipboard) {
      // Clipboard API not available
      triggerSnackbarFunc({
        snackbarMessage: "Link failed to copy",
        severity: "error",
      });
      return;
    }

    const text = `${blog?.attributes?.title}: https://my.hiredly.com/advice/${blog?.attributes?.slug}`;
    navigator.clipboard.writeText(text);
    triggerSnackbarFunc({
      snackbarMessage: "Link copied successfully",
      severity: "success",
    });
  }

  return (
    <GridContent>
      <GridTitle>
        <TitleStyled
          ref={ArticleContentRef}
          id="title"
          dangerouslySetInnerHTML={{ __html: blog?.attributes?.title }}
        />
      </GridTitle>
      <GridShortDescription>
        <ShortDescription
          dangerouslySetInnerHTML={{ __html: blog?.attributes?.excerpt }}
        />
      </GridShortDescription>
      <GridArticleDetails>
        <ArticleDetails>
          By {blog?.attributes?.author?.data?.attributes?.fullName} on{" "}
          {moment(blog?.attributes?.createdAt)
            .format("YYYY-MM-DD LT")
            .replace(" ", " at ")}
        </ArticleDetails>
        <ArticleDetails $italic>
          Updated on{" "}
          {moment(blog?.attributes?.updatedAt)
            .format("YYYY-MM-DD LT")
            .replace(" ", " at ")}
        </ArticleDetails>
      </GridArticleDetails>
      <GridShareIcons>
        <FacebookShareButton
          url={shareUrl}
          quote={title}
          id={"facebook-advice-link-sharing-button"}
        >
          <FacebookIcon size={32} round />
        </FacebookShareButton>
        <LinkedinIconStyled
          url={shareUrl}
          title={title}
          id={"linkedin-advice-link-sharing-button"}
        >
          <LinkedinIcon size={32} round />
        </LinkedinIconStyled>
        <TwitterIconStyled
          url={shareUrl}
          title={title}
          id={"twitter-advice-link-sharing-button"}
        >
          <TwitterIcon size={32} round />
        </TwitterIconStyled>
        <CustomIconContainer id={"copy-advice-link-sharing-button"}>
          <LinkIconStyled onClick={onCopyToClipboard} />
        </CustomIconContainer>
      </GridShareIcons>
      {adviceAd ? (
        <LinkStyled
          target="_blank"
          rel="noopener noreferrer"
          href={adviceAd?.attributes?.url}
          onClick={() => handleAdBannerClick()}
          // TODO: resolve TypeScript type error
          // @ts-ignore
          inView={inView}
        >
          <AdPlacement ref={ref}>
            <ImageAdBanner
              src={adviceAd?.attributes?.image?.data?.attributes?.url}
              alt="advice-page-advertisement"
            />
          </AdPlacement>
        </LinkStyled>
      ) : (
        <Fragment></Fragment>
      )}
      <GridArticleContent>
        <ArticleContent
          className={classes.root}
          dangerouslySetInnerHTML={{ __html: blog?.attributes?.content }}
        />
      </GridArticleContent>
      <GridArticleDetails>
        <ArticleDetails $italic>
          By {blog?.attributes?.author?.data?.attributes?.fullName} on{" "}
          {moment(blog.attributes.createdAt)
            .format("YYYY-MM-DD LT")
            .replace(" ", " at ")}
        </ArticleDetails>
      </GridArticleDetails>
    </GridContent>
  );
}
